import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { MessageSquare, Video, X } from 'lucide-react';
import { track } from '../utils/firebase';

const isAndroid = () => /android/i.test(navigator.userAgent);
const isInstalledApp = () => {
  const ua = navigator.userAgent;
  return /wv|WebView/i.test(ua) || window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
};
const INSTALL_DISMISSED_KEY = 'callchat_install_dismissed';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAndroid() && !isInstalledApp() && !localStorage.getItem(INSTALL_DISMISSED_KEY)) {
      const timer = setTimeout(() => setShowPopup(true), 7000);
      return () => clearTimeout(timer);
    }
  }, []);

  const dismissPopup = () => {
    setShowPopup(false);
    localStorage.setItem(INSTALL_DISMISSED_KEY, Date.now().toString());
  };

  const trackInstall = () => {
    track.installClick('login_page');
    try {
      const count = parseInt(localStorage.getItem('callchat_install_clicks') || '0', 10);
      localStorage.setItem('callchat_install_clicks', (count + 1).toString());
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/install`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: 'login_page', timestamp: new Date().toISOString() })
      }).catch(() => {});
    } catch (e) {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login({ email, password });
      toast.success('Login successful');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6 relative">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <MessageSquare className="w-10 h-10 text-[#4338CA]" />
            <Video className="w-10 h-10 text-[#4338CA]" />
          </div>
          <h1 className="text-4xl font-bold text-[#0F172A] mb-2 font-heading">CallChat</h1>
          <p className="text-[#475569]">Connect, Chat, and Collaborate</p>
        </div>

        <div className="bg-white rounded-md border border-[#E2E8F0] p-8">
          <h2 className="text-2xl font-bold mb-6 text-[#0F172A] font-heading">Welcome Back</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" data-testid="login-email-label">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password" data-testid="login-password-label">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="mt-1"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#4338CA] hover:bg-[#3730A3] text-white rounded-md"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          <div className="mt-3 text-right">
            <Link to="/forgot-password" className="text-sm text-[#4338CA] hover:underline" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>
          {!isInstalledApp() && (
          <a
            href="/CallChat.apk"
            download="CallChat.apk"
            onClick={trackInstall}
            className="w-full mt-4 rounded-lg bg-[#0F172A] hover:bg-[#1E293B] text-white font-semibold flex items-center justify-center gap-2 transition-all no-underline"
            data-testid="login-download-apk-button"
            style={{padding: '14px 16px', fontSize: '16px', animation: 'pulse-glow 1.5s ease-in-out 2'}}
          >
            <span role="img" aria-label="rocket">🚀</span> Install App — Faster, Safer & No Login Needed
          </a>
          )}
          {!isInstalledApp() && (
          <p className="mt-2 text-center text-xs tracking-wide" style={{color: '#64748B'}}>
            <span role="img" aria-label="bolt">⚡</span> Faster than browser &nbsp;&bull;&nbsp; <span role="img" aria-label="phone">📞</span> Better call quality
          </p>
          )}
          <style>{`
            @keyframes pulse-glow {
              0%, 100% { box-shadow: 0 0 0 0 rgba(15, 23, 42, 0); }
              50% { box-shadow: 0 0 16px 4px rgba(67, 56, 202, 0.35); }
            }
            @keyframes slide-up {
              from { transform: translateY(100%); opacity: 0; }
              to { transform: translateY(0); opacity: 1; }
            }
          `}</style>
          <p className="mt-6 text-center text-[#475569]">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#4338CA] hover:underline" data-testid="register-link">
              Sign up
            </Link>
          </p>
        </div>
      </div>

      {/* Auto-show install popup for Android users */}
      {showPopup && (
        <div
          className="fixed bottom-0 left-0 right-0 z-50 p-4"
          style={{animation: 'slide-up 0.4s ease-out'}}
          data-testid="install-popup"
        >
          <div className="max-w-md mx-auto bg-[#0F172A] text-white rounded-2xl p-5 shadow-2xl relative">
            <button
              onClick={dismissPopup}
              className="absolute top-3 right-3 text-white/60 hover:text-white"
              data-testid="install-popup-close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-gradient-to-br from-[#4338CA] to-[#6366F1] rounded-xl flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-base">Get CallChat App</p>
                <p className="text-white/60 text-xs">157 KB &bull; Installs in seconds</p>
              </div>
            </div>
            <p className="text-white/80 text-sm mb-4">
              <span role="img" aria-label="bolt">⚡</span> 3x faster &nbsp;&bull;&nbsp; <span role="img" aria-label="phone">📞</span> HD calls &nbsp;&bull;&nbsp; <span role="img" aria-label="bell">🔔</span> Push notifications
            </p>
            <a
              href="/CallChat.apk"
              download="CallChat.apk"
              onClick={() => { trackInstall(); dismissPopup(); }}
              className="w-full py-3 rounded-xl bg-[#4338CA] hover:bg-[#3730A3] text-white font-bold text-base flex items-center justify-center gap-2 no-underline transition-all"
              data-testid="install-popup-download"
            >
              <span role="img" aria-label="download">📥</span> Install Now
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
